import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import { useSaveRegistration } from "../../../api/hooks";
import { isRealisticPhone } from "../../../lib/contact";
import { ORGANIZATION_YEAR_MIN } from "../../../lib/mandate";
import { AFFILIATIONS_LIMIT, LONG_TEXT_LIMIT, wordLength } from "../../../lib/text";
import type { RegistrationProfile, TerritoryRegion } from "../../../types";
import {
  dropBlankPlaces,
  newLocationRow,
  validateLocation,
  type LocationErrors,
  type LocationRow,
} from "../../../lib/location";
import { LocationSection } from "./LocationSection";
import {
  NumberRow,
  SelectionField,
  TextareaRow,
  TextRow,
  YesNoRow,
} from "./controls";

/**
 * Step 1 — organization coordinates, contacts, the services offered and the
 * description. The service range (regions / sectors / org types
 * *served*) is captured in its own step. Saved via PATCH, advancing the
 * server-side registration_step to 3.
 */
export function OrganizationStep({
  profile,
  serviceTypes,
  territories,
  onSaved,
}: {
  profile: RegistrationProfile;
  serviceTypes: { value: string; label: string }[];
  territories: TerritoryRegion[];
  onSaved: (nextStep: number) => void;
}) {
  const { t } = useTranslation();
  const save = useSaveRegistration();
  const [form, setForm] = useState<RegistrationProfile>(profile);
  const [places, setPlaces] = useState<LocationRow[]>(() =>
    (profile.business_locations ?? []).map((p) =>
      newLocationRow(p.region, p.mrc, p.address ?? ""),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [locationErrors, setLocationErrors] = useState<LocationErrors>({});

  function set<K extends keyof RegistrationProfile>(
    key: K,
    value: RegistrationProfile[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }
  const str = (k: keyof RegistrationProfile) => (form[k] as string | null) ?? "";
  const arr = (k: keyof RegistrationProfile) =>
    (form[k] as string[] | undefined) ?? [];
  const onScope = (k: keyof RegistrationProfile) => (next: string[]) =>
    set(k, next as RegistrationProfile[typeof k]);
  const setField = (k: keyof RegistrationProfile, v: string) =>
    set(k, v as RegistrationProfile[typeof k]);
  const onOther = (k: keyof RegistrationProfile) => (v: string) =>
    set(k, v as RegistrationProfile[typeof k]);

  // Required fields mirror ExpertOrganizationForm.REQUIRED_FIELDS (old app).
  const REQUIRED_TEXT: (keyof RegistrationProfile)[] = [
    "organization_name",
    "neq",
    "description",
    "contact_primary_last_name",
    "contact_primary_first_name",
    "contact_primary_function",
    "contact_primary_email",
    "contact_primary_phone",
  ];

  // The four figures, required like the text fields: integers, so blank is
  // null and 0 is a valid count (registration_sync.REQUIRED_ORGANIZATION_FIGURES).
  const REQUIRED_FIGURES = [
    "founding_year",
    "dd_services_start_year",
    "total_employees",
    "dedicated_experts_count",
  ] as const;
  const YEAR_FIELDS = ["founding_year", "dd_services_start_year"] as const;
  const currentYear = new Date().getFullYear();

  const PHONE_FIELDS: (keyof RegistrationProfile)[] = [
    "phone",
    "contact_primary_phone",
    "contact_secondary_phone",
  ];

  const validate = (): Record<string, string> => {
    const req = t("expert.register.validation.required");
    const e: Record<string, string> = {};
    for (const k of REQUIRED_TEXT) if (!str(k).trim()) e[k] = req;
    for (const k of REQUIRED_FIGURES) if (form[k] == null) e[k] = req;
    // The years' range is the model's (ORGANIZATION_YEAR_MIN to this year).
    for (const k of YEAR_FIELDS) {
      const year = form[k] as number | null;
      if (year != null && (year < ORGANIZATION_YEAR_MIN || year > currentYear))
        e[k] = t("expert.register.validation.year_range", {
          min: ORGANIZATION_YEAR_MIN,
          max: currentYear,
        });
    }
    // Phone format — the same rule ExpertOrganizationForm and the DRF serializer
    // enforce, so the SPA rejects what the server would reject anyway.
    for (const k of PHONE_FIELDS)
      if (!isRealisticPhone(str(k)))
        e[k] = t("expert.register.validation.phone");
    if (arr("service_types").length === 0) e.service_types = req;
    // Counted as Word counts, like the server (expert/text_length.py).
    const tooLong = t("expert.register.validation.too_long", {
      limit: LONG_TEXT_LIMIT,
    });
    for (const k of ["description", "awards"] as const)
      if (wordLength(str(k)) > LONG_TEXT_LIMIT) e[k] = tooLong;
    // RENA: must be answered, and an org listed on the RENA is ineligible.
    if (form.on_rena == null) e.on_rena = req;
    else if (form.on_rena === true)
      e.on_rena = t("expert.register.validation.rena_ineligible");
    return e;
  };

  const handleContinue = () => {
    setError(null);
    const found = validate();
    const filledPlaces = dropBlankPlaces(places);
    if (filledPlaces.length !== places.length) setPlaces(filledPlaces);
    const foundLocation = validateLocation(t, territories, {
      region: form.head_office_region ?? "",
      mrc: form.head_office_mrc ?? "",
      places: filledPlaces,
    });
    setErrors(found);
    setLocationErrors(foundLocation);
    if (Object.keys(found).length > 0 || Object.keys(foundLocation).length > 0) {
      setError(t("expert.register.validation.fix_required"));
      return;
    }
    // The provinces answer belongs to the service-range step: leave it out so
    // this step never re-sends a stale copy of it.
    const { serves_other_provinces: _serves, other_provinces: _provinces, ...ownFields } =
      form;
    save.mutate(
      {
        ...ownFields,
        business_locations: filledPlaces.map(({ address, region, mrc }) => ({
          address,
          region,
          mrc,
        })),
        registration_step: 3,
      },
      {
        onSuccess: () => onSaved(1),
        onError: () => setError(t("auth.genericError")),
      },
    );
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">{t("expert.register.mandatoryNote")}</p>
      <section className="space-y-4">
        <h4 className="text-base font-semibold">
          {t("expert.register.eligibilityHeading")}
        </h4>
        <YesNoRow
          name="on_rena"
          label={t("expert.register.fields.on_rena")}
          value={form.on_rena}
          onChange={(v) => set("on_rena", v)}
          required
          error={errors.on_rena}
        />
      </section>

      <h4 className="text-base font-semibold">
        {t("expert.register.organizationContactHeading")}
      </h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextRow
          label={t("expert.register.fields.organization_name")}
          value={str("organization_name")}
          onChange={(v) => set("organization_name", v)}
          className="sm:col-span-2"
          required
          error={errors.organization_name}
        />
        <TextRow
          label={t("expert.register.fields.legal_name_req")}
          value={str("legal_name_req")}
          onChange={(v) => set("legal_name_req", v)}
        />
        <TextRow
          label={t("expert.register.fields.neq")}
          value={str("neq")}
          onChange={(v) => set("neq", v)}
          required
          error={errors.neq}
        />
        <TextRow
          label={t("expert.register.fields.website")}
          type="url"
          value={str("website")}
          onChange={(v) => set("website", v)}
        />
        <TextRow
          label={t("expert.register.fields.email")}
          type="email"
          value={str("email")}
          onChange={(v) => set("email", v)}
        />
        <TextRow
          label={t("expert.register.fields.phone")}
          type="tel"
          value={str("phone")}
          onChange={(v) => set("phone", v)}
          error={errors.phone}
        />
      </div>

      <SelectionField
        label={t("expert.register.fields.service_types")}
        options={serviceTypes}
        selected={arr("service_types")}
        onChange={onScope("service_types")}
        otherLabel={t("expert.register.otherServices")}
        otherOption="other"
        otherValue={str("service_types_other")}
        onOtherChange={onOther("service_types_other")}
        required
        error={errors.service_types}
      />

      <LocationSection
        territories={territories}
        address={str("head_office_address")}
        onAddress={(v) => set("head_office_address", v)}
        region={form.head_office_region ?? ""}
        onRegion={(v) => set("head_office_region", v || null)}
        mrc={form.head_office_mrc ?? ""}
        onMrc={(v) => set("head_office_mrc", v)}
        places={places}
        onPlaces={setPlaces}
        errors={locationErrors}
      />

      <h4 className="text-base font-semibold">{t("expert.register.contactsHeading")}</h4>
      <section className="space-y-3">
        <p className="text-sm font-medium">{t("expert.register.contactHeading")}</p>
        <ContactFields
          prefix="contact_primary"
          str={str}
          setField={setField}
          required
          errors={errors}
        />
      </section>

      <section className="space-y-3">
        <p className="text-sm font-medium">
          {t("expert.register.contactHeadingSecondary")}
        </p>
        <ContactFields
          prefix="contact_secondary"
          str={str}
          setField={setField}
          errors={errors}
        />
      </section>

      <section className="space-y-3">
        <p className="text-sm font-medium">{t("expert.register.profileHeading")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {/* description first, then the identity rows;
              "affiliations" relabelled to "Ordre professionnel d'affiliation". */}
          <TextareaRow
            label={t("expert.register.fields.description")}
            rows={4}
            value={str("description")}
            onChange={(v) => set("description", v)}
            className="sm:col-span-2"
            required
            error={errors.description}
            wordLimit={LONG_TEXT_LIMIT}
          />
          <NumberRow
            label={t("expert.register.fields.founding_year")}
            value={(form.founding_year as number | null) ?? null}
            onChange={(v) => set("founding_year", v)}
            min={ORGANIZATION_YEAR_MIN}
            max={currentYear}
            required
            error={errors.founding_year}
          />
          <NumberRow
            label={t("expert.register.fields.dd_services_start_year")}
            value={(form.dd_services_start_year as number | null) ?? null}
            onChange={(v) => set("dd_services_start_year", v)}
            min={ORGANIZATION_YEAR_MIN}
            max={currentYear}
            required
            error={errors.dd_services_start_year}
          />
          <NumberRow
            label={t("expert.register.fields.total_employees")}
            value={(form.total_employees as number | null) ?? null}
            onChange={(v) => set("total_employees", v)}
            required
            error={errors.total_employees}
          />
          <NumberRow
            label={t("expert.register.fields.dedicated_experts_count")}
            value={(form.dedicated_experts_count as number | null) ?? null}
            onChange={(v) => set("dedicated_experts_count", v)}
            required
            error={errors.dedicated_experts_count}
          />
          <TextareaRow
            label={t("expert.register.fields.awards")}
            rows={2}
            value={str("awards")}
            onChange={(v) => set("awards", v)}
            className="sm:col-span-2"
            error={errors.awards}
            wordLimit={LONG_TEXT_LIMIT}
          />
          <TextareaRow
            label={t("expert.register.fields.affiliations")}
            rows={2}
            value={str("affiliations")}
            onChange={(v) => set("affiliations", v)}
            className="sm:col-span-2"
            maxLength={AFFILIATIONS_LIMIT}
          />
        </div>
      </section>

      {error && <p className="text-sm font-medium text-destructive">{error}</p>}

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={save.isPending}>
          {t("expert.register.next")}
        </Button>
      </div>
    </div>
  );
}

/** One contact person (primary or secondary) — matches the old app's shared
 * `_contact_fields.html`: name, role, email, then phone / ext. / LinkedIn. */
function ContactFields({
  prefix,
  str,
  setField,
  required,
  errors,
}: {
  prefix: "contact_primary" | "contact_secondary";
  str: (k: keyof RegistrationProfile) => string;
  setField: (k: keyof RegistrationProfile, v: string) => void;
  required?: boolean;
  /** Required, not optional: the phone here is validated, so a caller that
   *  forgets this blocks the step with no field marked anywhere. */
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();
  const key = (name: string) =>
    `${prefix}_${name}` as keyof RegistrationProfile;
  const err = (name: string) => errors[`${prefix}_${name}`];

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextRow
        label={t("expert.register.contact.last_name")}
        value={str(key("last_name"))}
        onChange={(v) => setField(key("last_name"), v)}
        required={required}
        error={err("last_name")}
      />
      <TextRow
        label={t("expert.register.contact.first_name")}
        value={str(key("first_name"))}
        onChange={(v) => setField(key("first_name"), v)}
        required={required}
        error={err("first_name")}
      />
      <TextRow
        label={t("expert.register.contact.function")}
        value={str(key("function"))}
        onChange={(v) => setField(key("function"), v)}
        className="sm:col-span-2"
        required={required}
        error={err("function")}
      />
      <TextRow
        label={t("expert.register.contact.email")}
        type="email"
        value={str(key("email"))}
        onChange={(v) => setField(key("email"), v)}
        className="sm:col-span-2"
        required={required}
        error={err("email")}
      />
      <div className="grid gap-4 sm:col-span-2 sm:grid-cols-[1fr_7rem_1fr]">
        <TextRow
          label={t("expert.register.contact.phone")}
          type="tel"
          value={str(key("phone"))}
          onChange={(v) => setField(key("phone"), v)}
          required={required}
          error={err("phone")}
        />
        <TextRow
          label={t("expert.register.contact.extension")}
          value={str(key("extension"))}
          onChange={(v) => setField(key("extension"), v)}
        />
        <TextRow
          label={t("expert.register.contact.linkedin")}
          type="url"
          value={str(key("linkedin"))}
          onChange={(v) => setField(key("linkedin"), v)}
        />
      </div>
    </div>
  );
}
